/**
 * Hook: GET /api/t/{tenantSlug}/interview-prep/by-resource — Batch 30j-4.
 *
 * Returns the latest `interview_prep` row for `(candidate_id, jd_id)`, gated
 * on tab activation (`enabled`). Mirrors the post-30j-4 30j-3
 * `useInterviewAnalysis` shape with two differences:
 *
 *   1. Five-segment query key (no `type` segment — interview-prep doesn't
 *      have pre/post variants like interview-analysis).
 *   2. `jdId` is sent on the wire (`params.jd_id`); the backend's
 *      by-resource endpoint takes both `candidate_id` and `jd_id` per
 *      `SAAS_ARCHITECTURE.md:3782`.
 *
 * Durable rev (2026-06-17): Interview Prep was re-architected off the SSE
 * stream onto the durable job_queue worker. Delivery is now POLL-based, so
 * this hook polls every 3s while `status ∈ {queued, running}` and stops on
 * `complete` / `failed` — IDENTICAL to `useInterviewAnalysis`. (Previously
 * `refetchInterval: false`; the SSE stream WAS the update mechanism — that
 * stream is gone.)
 *
 * URL note: `/api/t/{tenantSlug}/interview-prep/...` per the tenant-router
 * canon (`backend/app/__init__.py:381` — `tenant_router` mounted at
 * `/api/t/{tenant_slug}`). Same canon 30j-3 fix-forward applied
 * (`f01708a` URL canonization).
 *
 * Hard rules:
 *   - 404 from `by-resource` is the LEGITIMATE empty state. Hook catches
 *     it at the queryFn boundary and returns `null`. Phase derivation
 *     downstream maps `null` → `"empty"`, NOT `"error"`.
 *   - 404s do NOT retry. Other errors retry up to 2 times.
 *   - `enabled` gates on `activeTab === "prep"` AND tenant/candidate/jd
 *     all set. When `enabled === false`, queryFn is NEVER invoked — this
 *     is the LCP guarantee for render-and-hide (Sec brief HARD-1 / S-30j4-17).
 *   - The wire endpoint takes `candidate_id` + `jd_id`. Server returns at
 *     most one row per `(candidate, jd)`; the prep dedup index enforces
 *     this at the DB layer (`SAAS_ARCHITECTURE.md:1765-1768`).
 *
 * Re-export: `InterviewPrepRow` is canonically declared in
 * `lib/derivePrepPhase.ts` (commit 2 — landed without circular dep) and
 * re-exported here for ergonomics. `useInterviewAnalysis` declares its
 * row inline; the two patterns coexist.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useInvalidateJournalOnComplete } from "@/hooks/queries/journal/useInvalidateJournalOnComplete";

export type { InterviewPrepRow } from "@/lib/derivePrepPhase";
import type { InterviewPrepRow } from "@/lib/derivePrepPhase";

interface UseInterviewPrepOpts {
  candidateId: string | undefined;
  jdId: string | undefined;
  /**
   * Tab-activation gate. When false, the queryFn is NEVER invoked
   * (no fetch). Pass `activeTab === "prep"` from the consumer.
   */
  enabled: boolean;
}

export function useInterviewPrep(
  opts: UseInterviewPrepOpts,
): UseQueryResult<InterviewPrepRow | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { candidateId, jdId, enabled } = opts;

  const query = useQuery<InterviewPrepRow | null, ApiError>({
    // Cache key isolates per-(tenant, candidate, jd). HARD-8 / S-30j4-17.
    queryKey: [
      "interview-prep",
      "by-resource",
      tenantSlug,
      candidateId,
      jdId,
    ],
    queryFn: async () => {
      try {
        const r = await apiClient.get<InterviewPrepRow>(
          `/api/t/${tenantSlug}/interview-prep/by-resource`,
          { params: { candidate_id: candidateId, jd_id: jdId } },
        );
        return r.data;
      } catch (err) {
        // 404 → empty row, not error. Same pattern as `useInterviewAnalysis`.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: enabled && !!tenantSlug && !!candidateId && !!jdId,
    // Durable rev: poll every 3s while the worker is generating (queued or
    // running), stop on complete/failed. Mirrors useInterviewAnalysis.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === "queued" || data.status === "running")) {
        return 3000;
      }
      return false;
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
    // Cached row stays "fresh" for 30s so a tab cycle does NOT trigger an
    // immediate refetch (which would visibly flicker between cached state
    // and refetch landing). Mirrors useInterviewAnalysis.
    staleTime: 30_000,
  });

  // Card 2 §7 — surface a freshly-completed prep in the Actions Log without
  // requiring navigation.
  useInvalidateJournalOnComplete(query.data?.status);

  return query;
}
